import { CdkTextareaAutosize } from "@angular/cdk/text-field";
import { AsyncPipe, JsonPipe, NgStyle, NgTemplateOutlet } from "@angular/common";
import { HttpResponse } from "@angular/common/http";
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from "@angular/core";
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatAutocomplete, MatAutocompleteTrigger } from "@angular/material/autocomplete";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatCard, MatCardContent } from "@angular/material/card";
import { MatCheckbox } from "@angular/material/checkbox";
import { MatOption } from "@angular/material/core";
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from "@angular/material/datepicker";
import { MatFormField, MatLabel, MatSuffix } from "@angular/material/form-field";
import { MatIcon } from "@angular/material/icon";
import { MatInput } from "@angular/material/input";
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
} from "@angular/material/table";
import { MatTooltip } from "@angular/material/tooltip";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, debounceTime, distinctUntilChanged, map, Observable, pairwise, startWith, Subscription } from "rxjs";
import { EpfService, MessageResponse } from "src/app/services/epf.service";
import { v4 as generateUUID } from "uuid";
import { ENVIRONMENT } from "../../../../../environments/environment";
import { EnergyConsult } from "../../../../classes/flow/request/EnergyConsult";
import { RequestState, RequestStates } from "../../../../classes/flow/request/RequestStates";
import { Coach } from "../../../../classes/flow/session/impl/Coach";
import { canAccesTemplate as accesCheck, ConsultviewTemplateRef } from "../../../../classes/user/Permissions";
import { CustomTimepickerComponent } from "../../../../components/custom-timepicker/custom-timepicker.component";
import { EnergyConsultComponent } from "../../../../components/energy-consult/energy-consult.component";
import { InputErrorComponent } from "../../../../components/error/error.component";
import { LinkRedirectComponent } from "../../../../components/link-redirect/link-redirect.component";
import { ExcludeModuleDirective } from "../../../../directives/exclude-module-directive";
import { ModuleDirective } from "../../../../directives/module-directive";
import { RoleDirective } from "../../../../directives/role-directive";
import { downloadFile } from "../../../../helpers/downloadFile";
import { MailDataTimelineComponent } from "../../../../mail-data-timeline/mail-data-timeline.component";
import { LocalDatePipe } from "../../../../pipes/date.pipe";
import { AddressService } from "../../../../services/address.service";
import { ApplicationService } from "../../../../services/application.service";
import { AuthService } from "../../../../services/auth.service";
import { CoachEnergyConsultService } from "../../../../services/coach-energy-consult.service";
import { DashboardDataService } from "../../../../services/dashboard-data.service";
import { DialogService } from "../../../../services/dialog.service";
import { EnergyConsultService } from "../../../../services/energy-consult.service";
import { ReportService } from "../../../../services/report.service";
import { SnackbarService } from "../../../../services/snackbar.service";
import { alphaValidator } from "../../../../validators/alpha";
import { houseNumberValidator } from "../../../../validators/houseNumber";
import { phoneNumberValidator } from "../../../../validators/phoneNumber";
import { postalCodeValidator } from "../../../../validators/postalCode";
import { trueAlphaNumeric } from "../../../../validators/trueAlphaNumeric";
import { convertDate } from "src/app/helpers/convertDate";

@Component({
  selector: "app-coach-energy-consult",
  templateUrl: "./coach-energy-consult.component.html",
  styleUrls: ["./coach-energy-consult.component.less"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatButton,
    RouterLink,
    MatIcon,
    EnergyConsultComponent,
    NgTemplateOutlet,
    RoleDirective,
    ModuleDirective,
    MatInput,
    CdkTextareaAutosize,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    InputErrorComponent,
    MatCheckbox,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatOption,
    ExcludeModuleDirective,
    MatCard,
    MatCardContent,
    MatIconButton,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    CustomTimepickerComponent,
    MatTooltip,
    NgStyle,
    MailDataTimelineComponent,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    LinkRedirectComponent,
    AsyncPipe,
    JsonPipe,
    TranslatePipe,
    LocalDatePipe,
  ],
})
export class CoachEnergyConsultComponent implements OnDestroy, AfterViewInit, OnInit {
  readonly application = inject(ApplicationService);
  private readonly translateService = inject(TranslateService);
  private readonly energyConsultService = inject(EnergyConsultService);
  private readonly route = inject(ActivatedRoute);
  readonly dialog = inject(DialogService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly coachEnergyConsultService = inject(CoachEnergyConsultService);
  private readonly reportService = inject(ReportService);
  private router = inject(Router);
  private readonly addressService = inject(AddressService);
  private readonly dashboardDataService = inject(DashboardDataService);
  private readonly snackbar = inject(SnackbarService);

  private auth = inject(AuthService);
  private epf = inject(EpfService);

  public redirectLink: string = ENVIRONMENT.REDIRECTS.find((redirect) => redirect[0] === "coachEnergyConsultComponent")?.[1] || "";
  public redirectTitle: string = ENVIRONMENT.REDIRECTS.find((redirect) => redirect[0] === "coachEnergyConsultComponent")?.[2] || "";
  private id: string = generateUUID();
  public energyConsult?: EnergyConsult;
  public isEditingDate = false;
  public formGroup = new FormGroup({
    date: new FormControl(new Date(), Validators.required),
    time: new FormControl("", Validators.required),
  });
  public rejectReasonControl = new FormControl("", Validators.required);
  public noteControl = new FormControl("", Validators.required);

  public pdfSendDate?: string;
  public sendDateAvailable = false;
  public fileNames: Array<{ name: string; extension: string; actionIDGet: string; actionIDDelete: string; fileSize: number }> = [];
  public coaches?: any;
  public changeCoachControl = new FormControl();
  public showActions = false;

  public fileSelected$ = new BehaviorSubject<string | undefined>(undefined);

  @ViewChild("pdfGenerateSuccess")
  private pdfGenerateSuccess!: TemplateRef<unknown>;

  @ViewChild("savedNoteSuccess")
  private savedNoteSuccess!: TemplateRef<unknown>;

  @ViewChild("attachmentFileInput")
  private fileInput?: ElementRef<HTMLInputElement>;

  @ViewChild("editDialog")
  public editDialog!: TemplateRef<unknown>;

  @ViewChild("ckdSuccesDialog")
  public ckdSuccesDialog!: TemplateRef<unknown>;

  @ViewChild("ckdConfirmDialog")
  public ckdConfirmDialog!: TemplateRef<unknown>;

  public maxDate: Date;

  public editFormGroup = new FormGroup({
    firstName: new FormControl<string | null>(null, [Validators.required, Validators.minLength(2), alphaValidator]),
    lastName: new FormControl<string | null>(null, [Validators.required, Validators.minLength(2), alphaValidator]),
    email: new FormControl<string | null>(null, [Validators.required, Validators.email]),
    usePhoneNumber: new FormControl<boolean | null>(null),
    address: new FormGroup({
      postalCode: new FormControl<string | null>(null, [Validators.required, postalCodeValidator]),
      houseNumber: new FormControl<number | null>(null, [Validators.required, houseNumberValidator]),
      houseNumberSuffix: new FormControl<string | null>(null, [trueAlphaNumeric, Validators.maxLength(5)]),
    }),
    phoneNumber: new FormControl<string | null>({ disabled: true, value: null }, [Validators.required, phoneNumberValidator]),
    streetName: new FormControl<string | null>(null, [alphaValidator, Validators.required]),
    town: new FormControl<string | null>(null, [alphaValidator, Validators.required]),
  });
  public filteredOptions: Observable<Coach[]> | null = null;
  public editFormPNUseSub?: Subscription;
  public filterPostalCodeInput?: Subscription;
  public addressAutoFillSubscription?: Subscription;
  public requestStates: RequestState[] = [];

  private fileTypes: string[] = [
    "image/apng",
    "image/avif",
    "image/gif",
    "image/jpeg",
    "image/png",
    "image/svg+xml",
    "image/webp",
    "application/pdf",
  ];

  //templates for page
  templateNames: ConsultviewTemplateRef[] = [
    "changeCoach",
    "ckdTemplate",
    "scheduleVisit",
    "rejectAndPutback",
    "editConsultData",
    "downloadReport",
    "fileUpload",
    "fillReport",
    "mailDataTimeline",
    "notesTemplate",
    "claimTemplate",
  ];

  accessibleTemplates: string[] = [];
  @ViewChildren(TemplateRef) templates!: QueryList<TemplateRef<any>>;

  public getTemplate(templateName: string): TemplateRef<any> | null {
    return this.templates.find((template: any) => template._declarationTContainer?.localNames?.includes(templateName)) || null;
  }

  public canAccessTemplate(template: string): boolean {
    return accesCheck(template as ConsultviewTemplateRef, this.auth.roleName, this.energyConsult!.state);
  }

  public constructor() {
    this.maxDate = new Date();
    this.maxDate.setFullYear(new Date().getFullYear() + 1);
  }

  public ngOnInit(): void {
    this.filteredOptions = this.changeCoachControl.valueChanges.pipe(
      startWith(""),
      map((value) => {
        const name = typeof value === "string" ? value : value?.name;
        return name ? this._filter(name as string) : this.coaches.slice();
      }),
    );
  }

  private _filter(name: string): Coach[] {
    const filterValue = name.toLowerCase();

    return this.coaches.filter(
      (coach: { firstName: string; lastName: string }) =>
        coach.firstName.toLowerCase().includes(filterValue) || coach.lastName.toLowerCase().includes(filterValue),
    );
  }

  public ngAfterViewInit(): void {
    const route = this.route.params.subscribe(async () => {
      this.initialize();
    });
    this.application.subscribe(this.id, route);
  }

  public ngOnDestroy(): void {
    this.application.unsubscribe(this.id);
    if (this.editFormPNUseSub !== undefined) this.editFormPNUseSub.unsubscribe();
    if (this.filterPostalCodeInput !== undefined) this.filterPostalCodeInput.unsubscribe();
    if (this.addressAutoFillSubscription !== undefined) this.addressAutoFillSubscription.unsubscribe();
  }

  public async initialize(): Promise<void> {
    this.application.setLoading(true);
    await this.initEnergyConsult(this.route.snapshot.params.id);
    if (!this.energyConsult) throw new Error("EnergyConsult not present");
    this.initializeFormGroup();
    this.checkSendReportDate();
    if (
      this.energyConsult.state.name === "FilledIn" ||
      this.energyConsult.state.name === "Done" ||
      this.energyConsult.state.name === "PendingReject"
    ) {
      this.getfileNames();
    }
    this.application.setLoading(false);
  }

  public setEditingDate(isEditingDate: boolean): void {
    this.isEditingDate = isEditingDate;
  }

  public newFileSelected() {
    if (this.fileTypes.includes(this.fileInput!.nativeElement.files!.item(0)!.type)) {
      this.fileSelected$.next(this.fileInput?.nativeElement?.files?.item(0)?.name);
    } else {
      const extension = this.fileInput!.nativeElement.files!.item(0)!.name.split(".").at(-1);
      this.snackbarService.open(
        this.translateService.instant("COMPONENTS.EVERYWHERE.FILEMANAGEMENT.FILE_TYPE_ERROR", { fileType: "." + extension }),
        5000,
      );
    }
  }

  /**
   * Queries for attachments metaData + action IDs
   * stores them in this.fileNames
   * can be recalled to update the shown files on the page
   */
  public async getfileNames() {
    try {
      this.application.setLoading(true);
      const res = await this.coachEnergyConsultService.getAttachmentList(this.energyConsult!);
      const fileObjects = res.filter((file) => {
        return !file.name.includes("signature.png");
      });
      this.fileNames = fileObjects;
    } catch (error) {
      console.error(error);
    } finally {
      this.application.setLoading(false);
    }
  }

  /**
   * Uploads the given file object to the content endpoint
   * @param files value of the file input from html
   */
  public async uploadAttachment(files: FileList | null) {
    if (!files?.length) {
      return;
    }
    try {
      this.application.setLoading(true);
      let totalFileSize = 0;
      Array.from(files).forEach((file) => {
        totalFileSize += file.size;
      });
      this.fileNames.forEach((item) => {
        totalFileSize += item.fileSize;
      });
      if (totalFileSize > 18000000) {
        this.snackbarService.open(this.translateService.instant("COMPONENTS.EVERYWHERE.FILEMANAGEMENT.TOO_LARGE"));
        this.application.setLoading(false);
        return;
      }

      for (const file of Array.from(files)) {
        const formData = new FormData();
        formData.append("contentid", this.energyConsult!.ffwdContentId!);
        formData.append("contentpartid", this.energyConsult!.ffwdContentPartId!);
        formData.append("attachment", "true");
        formData.append("file", file);
        const reponse: HttpResponse<{ postDataAsJSON: string }> = await this.coachEnergyConsultService.postRequest("content", formData, {
          observe: "response",
          withCredentials: true,
        });
        if (reponse.status !== 200) {
          this.snackbarService.open(this.translateService.instant("COMPONENTS.EVERYWHERE.FILEMANAGEMENT.ERROR"));
        }
        if (reponse.body?.postDataAsJSON) {
          const content: { messages: MessageResponse[]; result: string } = JSON.parse(reponse.body.postDataAsJSON);
          this.epf.processMessages(content.messages);
        }
      }
      await this.initialize();
    } catch (error) {
      console.error(error);
    } finally {
      this.application.setLoading(false);
    }
  }

  /**
   * Queries for file data and downloads it for the user
   * @param getId the download action id queried for earlier
   * @param fileName name to give the file
   */
  public async downloadTableFile(getId: string, fileName: string) {
    try {
      this.application.setLoading(true);
      const res = await this.coachEnergyConsultService.postRequest(
        "action",
        {
          FFWDActionID: getId,
        },
        {
          observe: "response",
          responseType: "blob",
        },
      );
      if (res instanceof Error) {
        this.snackbarService.error();
      }

      const objectURL = URL.createObjectURL(res.body);
      const a = document.createElement("a");
      a.href = objectURL;
      a.download = fileName;
      a.click();
      a.remove();
      URL.revokeObjectURL(objectURL);
    } catch (error) {
      console.error(error);
    } finally {
      this.application.setLoading(false);
    }
  }

  /**
   * Sends the id to the action endpoint to trigger delete action
   * @param deleteId the delete action id queried for earlier
   */
  public async deleteTableFile(deleteId: string) {
    try {
      this.application.setLoading(true);
      const res = await this.coachEnergyConsultService.postRequest("action", {
        FFWDActionID: deleteId,
      });
      if (res instanceof Error) {
        this.snackbarService.error();
      }
      await this.getfileNames();
    } catch (error) {
      console.error(error);
    } finally {
      this.application.setLoading(false);
    }
  }

  /**
   * Initializes the form group of the date
   */
  private initializeFormGroup() {
    const appointmentDate = this.energyConsult?.appointmentDate ? new Date(this.energyConsult.appointmentDate) : undefined;
    const today = new Date();
    this.formGroup.patchValue({
      date: appointmentDate && appointmentDate < today ? today : appointmentDate,
      time: appointmentDate?.toTimeString().substring(0, 5) ?? "",
    });
  }

  /**
   * gets the date of when the report was send
   */
  private async checkSendReportDate() {
    if (this.energyConsult) {
      try {
        this.application.setLoading(true);
        this.pdfSendDate = this.energyConsult.lastSendDate || undefined;
        this.sendDateAvailable = true;
      } catch (error) {
        console.error(error);
      } finally {
        this.application.setLoading(false);
      }
    }
  }

  private async setSendReportDate() {
    if (this.energyConsult) {
      this.application.setLoading(true);
      this.energyConsult = await this.coachEnergyConsultService.setSendReportDate(this.energyConsult);
      await this.initEnergyConsult(this.energyConsult.id);
    }
  }

  /**
   * Sends a mail and uses the loading component to prevent the user from clicking multiple times
   */
  public async sendPDFMail() {
    if (this.energyConsult) {
      try {
        this.application.setLoading(true);
        await this.energyConsultService.sendPDFToResident(this.energyConsult.id);
        this.openPDFSuccessDialog();
        await this.setSendReportDate();
        await this.checkSendReportDate();
      } catch (error) {
        this.snackbarService.open(this.translateService.instant("COMPONENTS.COACH_REQUEST.CLAIM.FAILURE_MESSAGE"));
      }
      this.application.setLoading(false);
    }
  }

  /**
   * Opens a dialog if the pdf is successfull generated
   */
  private openPDFSuccessDialog() {
    this.dialog.open({
      template: this.pdfGenerateSuccess,
    });
  }

  /**
   * Saves the chosen date
   */
  public async saveDate() {
    if (this.energyConsult) {
      const date: Date = this.getGivenDate();
      try {
        this.application.setLoading(true);
        this.energyConsult = await this.coachEnergyConsultService.saveDate(this.energyConsult, date, this.requestStates);
        await this.initEnergyConsult(this.energyConsult.id);
      } catch (error) {
        this.isEditingDate = false;
        this.energyConsult.appointmentDate = convertDate(date);
        console.error(error);
      } finally {
        this.application.setLoading(false);
      }
    }
  }

  /**
   * fills the preferred time slot into appointment input fiels
   * @param index index of time slot in array
   */
  public fillInPreferredTimeSlot(index: number) {
    const startTime = this.energyConsult?.extraProperties?.preferredTimeSlots?.at(index)?.startTime;
    const time = startTime ? startTime.slice(11, 16) : null;
    const date = startTime ? new Date(startTime) : null;

    try {
      this.formGroup.controls["date"].setValue(date);
      this.formGroup.controls["time"].setValue(time);
      this.formGroup.controls["date"].markAsTouched();
      this.formGroup.controls["time"].markAsTouched();
    } catch {
      this.snackbarService.error();
    }
  }

  /**
   * Claim a energyConsult
   */
  public async claimEnergyConsult() {
    if (this.energyConsult) {
      try {
        this.application.setLoading(true);
        const coach = new Coach({ id: this.auth.coachId ?? undefined, userEmail: "" });
        this.energyConsult = await this.coachEnergyConsultService.AcceptEnergyConsult(this.energyConsult, coach, this.requestStates);

        await this.initEnergyConsult(this.energyConsult.id);
      } catch (error) {
        console.error(error);
      } finally {
        this.application.setLoading(false);
      }
    }
  }

  /**
   * Recovers a energyConsult
   */
  public async recoverEnergyConsult() {
    if (this.energyConsult) {
      try {
        this.application.setLoading(true);
        this.energyConsult.coach = undefined;
        await this.setEnergyConsultState(RequestStates.NEW);
      } catch (error) {
        console.error(error);
      } finally {
        this.application.setLoading(false);
      }
    }
  }

  public async finishEnergyConsult() {
    if (this.energyConsult) {
      try {
        this.application.setLoading(true);
        await this.setEnergyConsultState(RequestStates.DONE);
      } catch (error) {
        console.error(error);
      } finally {
        this.application.setLoading(false);
      }
    }
  }

  /**
   * Deletes a energyConsult
   */
  public async deleteEnergyConsult() {
    try {
      this.application.setLoading(true);
      await this.setEnergyConsultState(RequestStates.PENDING_DELETED);
    } catch (error) {
      console.error(error);
    } finally {
      this.application.setLoading(false);
    }
  }

  /**
   * Sets the state of the energyConsult
   * @param state The state of the energyConsult
   * @param key The property of the energyConsult to update
   */
  private async setEnergyConsultState(state: RequestStates) {
    if (this.energyConsult) {
      try {
        this.application.setLoading(true);
        this.energyConsult = await this.energyConsultService.setState(this.energyConsult, state, this.requestStates);
        await this.initEnergyConsult(this.energyConsult.id);
      } catch (error) {
        console.error(error);
      } finally {
        this.application.setLoading(false);
      }
    }
  }

  /**
   * Saves the reject reason for the energyConsult
   */
  public async rejectEnergyConsult(sendMail: boolean) {
    if (this.energyConsult && this.rejectReasonControl.valid) {
      try {
        this.application.setLoading(true);
        this.dialog.close();
        const reason = this.rejectReasonControl.value ?? "";
        this.energyConsult = await this.coachEnergyConsultService.rejectEnergyConsult(this.energyConsult, sendMail, reason, this.requestStates);
        await this.initEnergyConsult(this.energyConsult.id);
      } catch (error) {
        console.error(error);
      } finally {
        this.application.setLoading(false);
      }
    }
  }

  /**
   * Checks if it has an alert when the energyConsult is canceled, pending rejected or disapproved.
   */
  public get hasAlert(): boolean {
    return (
      !!this.energyConsult &&
      [RequestStates.PENDING_REJECT, RequestStates.CANCELED, RequestStates.DISAPPROVED].includes(this.energyConsult.state.name)
    );
  }

  /**
   * Get the data from the energyConsult
   * Parse the date to locale
   */
  private async initEnergyConsult(id: number) {
    try {
      this.application.setLoading(true);
      // const energyConsult = await this.energyConsultService.loadById(id);
      const { energyConsult, requestStates, coaches } = await this.epf.getEnergyConsult(id);
      this.requestStates = requestStates;
      this.coaches = coaches;

      if (!energyConsult) {
        this.snackbarService.error();
        this.router.navigate([this.auth.roleName === "resident" ? "/" : "/content"]);
        return;
      }

      // if (!energyConsult.isActive && this.auth.roleName !== "coordinator" && !ENVIRONMENT.MODULES.includes("SHOW_CONTACT_INFO_ON_NEW_REQUESTS")) {
      //   energyConsult.resident = undefined;
      // }

      this.energyConsult = energyConsult;
      this.dashboardDataService.updateSingleEnergyConsult(energyConsult);

      if (this.energyConsult?.extraProperties?.note) {
        this.noteControl.setValue(this.energyConsult?.extraProperties?.note);
      }
      this.showActions = this.checkShowActions();
      this.application.setLoading(false);
      //setting templates to null to prevent duplicate templates after calling initedEnergyConsult again
      this.accessibleTemplates = [];
      this.templateNames.forEach((name) => {
        if (this.canAccessTemplate(name)) {
          this.accessibleTemplates.push(name);
        }
      });
    } catch (error) {
      console.error(error);
    } finally {
      this.application.setLoading(false);
    }
  }

  /**
   * Gets the selected date from the date form
   * @returns The selected date
   */
  private getGivenDate(): Date {
    const date: Date = this.formGroup.value.date!;
    date.setHours(+this.formGroup.value.time!.substr(0, 2), +this.formGroup.value.time!.substr(3, 2));
    return date;
  }

  /**
   * Assign a coach to a energyConsult (being a coordinator)
   * @param Coach the new coach
   */
  public async assignCoach(coach: Coach) {
    try {
      this.application.setLoading(true);
      if (this.energyConsult) {
        this.energyConsult = await this.coachEnergyConsultService.AcceptEnergyConsult(this.energyConsult, coach, this.requestStates);
        this.initEnergyConsult(this.energyConsult.id);
        this.dialog.close();
      }
    } catch (error) {
      this.snackbar.open(this.translateService.instant("COMPONENTS.EVERYWHERE.ERROR.CHANGE_COACH"));
      console.error(error);
    } finally {
      this.application.setLoading(false);
    }
  }

  /**
   * First call gets all coaches and opens dialog, other calls just open dialog
   * @param TemplateRef the dialog template name
   */
  public async changeCoachClickHandler(template: TemplateRef<any>) {
    this.changeCoachControl.patchValue(this.energyConsult?.coach);

    this.dialog.show(template);
  }

  async changeNote(button: MatButton) {
    button.disabled = true;
    if (!this.energyConsult) return;
    try {
      this.application.setLoading(true);
      const value = this.noteControl.getRawValue();
      const payload = this.energyConsult.duplicate();
      if (!payload.extraProperties) throw new Error();

      payload.extraProperties.note = value || undefined;
      const result = await this.epf.updateEnergyConsult(payload);
      this.energyConsult = result;
      this.snackbarService.open(this.translateService.instant("COMPONENTS.COACH_REQUEST.DIALOGS.NOTE.SUCCESS_MESSAGE"), 5000);
    } catch (error) {
      this.snackbarService.error();
    } finally {
      this.application.setLoading(false);
      button.disabled = false;
    }
    if (this.energyConsult) this.dashboardDataService.updateSingleEnergyConsult(this.energyConsult);
  }
  /**
   * Opens a dialog if the pdf is successfull generated
   */
  private openNoteSuccessDialog() {
    this.dialog.open({
      template: this.savedNoteSuccess,
    });
  }
  /**
   * Concat first and last name
   * @returns String with fullname
   */
  public displayCoachName(coach: Coach) {
    if (coach) return coach.firstName + " " + coach.lastName;
    return "";
  }

  /**
   * sends the report as pdf to the coach for review
   */
  public async downloadReportAsPDF() {
    if (this.energyConsult) {
      try {
        this.application.setLoading(true);
        const pdf = await this.reportService.getPdfFile(this.energyConsult.id);
        if (!pdf) throw new Error("No PDF");
        downloadFile(pdf, this.translateService.instant("COMPONENTS.COACH_REQUEST.REPORT"), "application/pdf");
      } catch (error) {
        this.snackbarService.open(this.translateService.instant("COMPONENTS.EVERYWHERE.ERROR.GENERIC"), 5000);
      }
      this.application.setLoading(false);
    }
  }

  /**
   *
   * @param number
   * @returns
   */
  public formatSize(number: number) {
    return Intl.NumberFormat("en", {
      notation: "compact",
      style: "unit",
      unit: "byte",
      unitDisplay: "narrow",
    }).format(number);
  }

  /**
   * Open an edit energyConsult Dialog
   * @param energyConsultId The id of the energyConsult
   */
  public editRequestDialog(): void {
    if (this.energyConsult === undefined) return;
    this.editFormGroup.controls.firstName.setValue(this.energyConsult.resident?.firstName ?? null);
    this.editFormGroup.controls.lastName.setValue(this.energyConsult.resident?.lastName ?? null);
    this.editFormGroup.controls.email.setValue(this.energyConsult.resident?.email ?? null);
    this.editFormGroup.controls.usePhoneNumber.setValue(
      this.energyConsult.resident?.phoneNumber === null || this.energyConsult.resident?.phoneNumber === "" ? false : true,
    );
    this.editFormGroup.controls.phoneNumber.setValue(this.energyConsult.resident?.phoneNumber ?? null);
    this.editFormGroup.controls.usePhoneNumber.value
      ? this.editFormGroup.controls.phoneNumber.enable()
      : this.editFormGroup.controls.phoneNumber.disable();
    this.editFormGroup.controls.address.controls.postalCode.setValue(this.energyConsult.postalCode ?? null);
    this.editFormGroup.controls.address.controls.houseNumber.setValue(this.energyConsult.houseNumber ?? null);
    this.editFormGroup.controls.address.controls.houseNumberSuffix.setValue(this.energyConsult.houseNumberSuffix ?? null);
    this.editFormGroup.controls.streetName.setValue(this.energyConsult.extraProperties?.streetname ?? null);
    this.editFormGroup.controls.town.setValue(this.energyConsult.extraProperties?.addressdetails?.town ?? null);

    if (this.editFormPNUseSub === undefined) {
      this.editFormPNUseSub = this.editFormGroup.controls.usePhoneNumber.valueChanges.subscribe((value) => {
        value ? this.editFormGroup.controls.phoneNumber.enable() : this.editFormGroup.controls.phoneNumber.disable();
      });
    }

    if (this.filterPostalCodeInput === undefined) {
      this.filterPostalCodeInput = this.editFormGroup.controls.address.controls.postalCode.valueChanges
        .pipe(startWith(""), pairwise())
        .subscribe(([prevValue, newValue]) => {
          if (prevValue === newValue) return;
          this.editFormGroup.controls.address.controls.postalCode.setValue(
            /^(?:[0-9]|$){1,4}(?:[a-z]|$){0,2}$/i.test(newValue ?? "") ? (newValue ?? "").toUpperCase() : (prevValue ?? "").toUpperCase(),
          );
        });
    }

    if (this.addressAutoFillSubscription === undefined) {
      this.addressAutoFillSubscription = this.editFormGroup.controls.address.valueChanges
        .pipe(debounceTime(750), distinctUntilChanged())
        .subscribe(() => {
          this.autoFillAddress();
        });
    }

    this.dialog.open({
      template: this.editDialog,
      data: {
        EC: this.energyConsult,
      },
    });
  }

  private async autoFillAddress() {
    const addressCtrls = this.editFormGroup.controls.address.controls;
    const streetNameCtrl = this.editFormGroup.controls.streetName;
    const townCtrl = this.editFormGroup.controls.town;

    if (!addressCtrls.postalCode.valid || !addressCtrls.houseNumber.valid || !addressCtrls.houseNumberSuffix.valid) return;
    const postalCode = addressCtrls.postalCode.value;
    const houseNumber = addressCtrls.houseNumber.value;
    const houseNumberSuffix = addressCtrls.houseNumberSuffix.value;

    if (!postalCode || !houseNumber) {
      return;
    }

    const address = await this.addressService.lookup(postalCode, houseNumber.toString(), houseNumberSuffix);
    streetNameCtrl.patchValue(null, { emitEvent: false });
    townCtrl.patchValue(null, { emitEvent: false });

    streetNameCtrl.enable({ emitEvent: false });
    townCtrl.enable({ emitEvent: false });

    if (address) {
      streetNameCtrl.patchValue(address.streetName, { emitEvent: false });
      townCtrl.patchValue(address.town, { emitEvent: false });
    } else {
      streetNameCtrl.setErrors({ streetNameNotFound: true }, { emitEvent: false });
      townCtrl.setErrors({ townNotFound: true }, { emitEvent: false });
    }

    streetNameCtrl.markAsTouched();
    townCtrl.markAsTouched();
  }

  public async saveEditedRequest() {
    if (this.energyConsult === undefined) return;
    const payload = this.energyConsult.duplicate();

    const values = this.editFormGroup.value;
    if (payload.resident) {
      payload.resident.firstName = values.firstName ?? payload.resident.firstName;
      payload.resident.lastName = values.lastName ?? payload.resident.lastName;
      payload.resident.email = values.email ?? payload.resident.email;
      payload.resident.phoneNumber = values.phoneNumber ?? null;
    }
    payload.postalCode = values.address?.postalCode ?? payload.postalCode;
    payload.houseNumber = values.address?.houseNumber ?? payload.houseNumber;
    payload.houseNumberSuffix = values.address?.houseNumberSuffix ?? undefined;

    if (payload.extraProperties?.addressdetails)
      payload.extraProperties.addressdetails.town = values.town ?? payload.extraProperties?.addressdetails?.town;

    try {
      const result = await this.epf.updateEnergyConsult(payload);
      this.energyConsult = result;
    } catch {
      this.snackbarService.error();
    }

    this.initEnergyConsult(this.energyConsult.id);
  }

  public canEditConsult(): boolean {
    if (
      (this.auth.roleName === "coach" &&
        (this.energyConsult?.state.name === RequestStates.PICKED || this.energyConsult?.state.name === RequestStates.DATE)) ||
      (this.auth.roleName === "coordinator" &&
        (this.energyConsult?.state.name === RequestStates.NEW ||
          this.energyConsult?.state.name === RequestStates.PICKED ||
          this.energyConsult?.state.name === RequestStates.DATE))
    )
      return true;
    return false;
  }

  public checkShowActions(): boolean {
    if (
      this.auth.coachId &&
      this.auth.roleName === "coach" &&
      !this.energyConsult?.isActiveCoach(this.auth.coachId) &&
      this.energyConsult?.state.name !== RequestStates.NEW
    )
      return false;
    return true;
  }

  public openCkdConfirmDialog() {
    this.dialog.open({ template: this.ckdConfirmDialog });
  }

  public async confirmCkdEnergyConsult() {
    if (!this.energyConsult?.id) return;
    this.application.setLoading(true);
    try {
      const res = await this.energyConsultService.ckdEC(this.energyConsult);
      this.dialog.close(); //close out confirm dialogue
      if (!res.ckdData.requestIDNew) {
        this.snackbarService.error();
      } else {
        this.dialog.open({ template: this.ckdSuccesDialog, data: { newID: res.ckdData.requestIDNew } });
        await this.initEnergyConsult(this.energyConsult.id);
      }
    } catch (error) {
      this.snackbarService.error();
    }
    this.application.setLoading(false);
  }
}
